import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';


import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import * as XLSX from 'xlsx';
// Okta girişi şimdilik devre dışı - e-posta/şifre girişine geçildi
// import oktaAuth from './okta';

// const API_URL =  'http://localhost:3001';
const API_URL = process.env.REACT_APP_API_URL || 'https://getir-api.vercel.app';

// Grafik renkleri - tek seri için mor tema, çoklu seri için birbirinden ayırt edilebilir renkler
const COLORS = ['#7c3aed', '#a855f7', '#c084fc', '#e879f9', '#f0abfc', '#8b5cf6', '#6d28d9', '#5b21b6'];
const MULTI_SERIES_COLORS = ['#a855f7', '#06b6d4', '#f59e0b', '#ec4899', '#22c55e', '#3b82f6', '#ef4444', '#eab308'];

// Markdown tabloları için özel bileşenler
const MarkdownComponents = {
  table: ({ children }) => (
    <div className="table-container">
      <table>{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead>{children}</thead>,
  tbody: ({ children }) => <tbody>{children}</tbody>,
  tr: ({ children }) => <tr>{children}</tr>,
  th: ({ children }) => <th>{children}</th>,
  td: ({ children }) => <td>{children}</td>,
};

// Ay sıralaması
const MONTH_ORDER = {
  'OCAK': 1, 'ŞUBAT': 2, 'MART': 3, 'NİSAN': 4, 'MAYIS': 5, 'HAZİRAN': 6,
  'TEMMUZ': 7, 'AĞUSTOS': 8, 'EYLÜL': 9, 'EKİM': 10, 'KASIM': 11, 'ARALIK': 12,
  'JANUARY': 1, 'FEBRUARY': 2, 'MARCH': 3, 'APRIL': 4, 'MAY': 5, 'JUNE': 6,
  'JULY': 7, 'AUGUST': 8, 'SEPTEMBER': 9, 'OCTOBER': 10, 'NOVEMBER': 11, 'DECEMBER': 12
};

// Tarih formatını kontrol et (YYYY-MM-DD veya DD.MM.YYYY gibi)
const isDateString = (str) => {
  if (!str) return false;
  const datePatterns = [
    /^\d{4}-\d{2}-\d{2}$/,  // 2024-01-15
    /^\d{2}\.\d{2}\.\d{4}$/, // 15.01.2024
    /^\d{2}\/\d{2}\/\d{4}$/, // 15/01/2024
  ];
  return datePatterns.some(pattern => pattern.test(str));
};

// Tarihi Date objesine çevir
const parseDate = (str) => {
  if (!str) return new Date(0);
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
    return new Date(str);
  }
  if (/^\d{2}\.\d{2}\.\d{4}$/.test(str)) {
    const [day, month, year] = str.split('.');
    return new Date(`${year}-${month}-${day}`);
  }
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(str)) {
    const [day, month, year] = str.split('/');
    return new Date(`${year}-${month}-${day}`);
  }
  return new Date(str);
};

// Excel'e export fonksiyonu
const exportToExcel = (tableData) => {
  if (!tableData || !tableData.data || tableData.data.length === 0) return;

  // _display değerlerini kullanarak orijinal formatı koru
  const exportData = tableData.data.map(row => {
    const exportRow = {};
    tableData.headers.forEach(header => {
      const displayKey = `${header}_display`;
      const displayVal = row[displayKey];
      // _display varsa onu kullan (orijinal format), yoksa ham değeri
      exportRow[header] = displayVal !== undefined ? String(displayVal).replace(/\*\*/g, '').trim() : (row[header] ?? '');
    });
    return exportRow;
  });

  const ws = XLSX.utils.json_to_sheet(exportData, { header: tableData.headers });
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Veri');

  const now = new Date();
  const dateStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  XLSX.writeFile(wb, `tablo-export-${dateStr}.xlsx`);
};

// Markdown tablosunu parse et
const parseMarkdownTable = (content) => {
  const lines = content.split('\n').filter(line => line.trim().startsWith('|'));
  if (lines.length < 3) return null;

  const headers = lines[0].split('|').filter(h => h.trim()).map(h => h.trim());
  const dataRows = lines.slice(2); // İlk satır header, ikinci satır separator

  let data = dataRows.map(row => {
    // Pipe ile split et, sadece baştaki ve sondaki boş elemanları kaldır (aradaki boş hücreleri KORU)
    const rawParts = row.split('|');
    if (rawParts[0].trim() === '') rawParts.shift();
    if (rawParts.length > 0 && rawParts[rawParts.length - 1].trim() === '') rawParts.pop();
    const cells = rawParts.map(c => c.trim());
    const obj = {};
    headers.forEach((header, i) => {
      let value = cells[i] || '';
      
      // Tarih formatı kontrolü - tarihleri string olarak bırak
      const isDate = /^\d{4}-\d{2}-\d{2}/.test(value) || 
                     /^\d{2}\.\d{2}\.\d{4}/.test(value) ||
                     /^\d{2}\/\d{2}\/\d{4}/.test(value);
      
      if (isDate) {
        // Tarih ise string olarak bırak
        obj[header] = value;
        obj[`${header}_display`] = value;
        obj[`${header}_raw`] = value;
      } else {
        // Önce ** bold işaretlerini temizle
        let cleanValue = value.replace(/\*\*/g, '').trim();

        // Para birimi sembollerini temizle (TL kelimesini, ₺, €, $, % işaretlerini)
        // NOT: Sadece "TL" kelimesini sil, tek başına T veya L harflerini DEĞİL!
        cleanValue = cleanValue.replace(/\s*TL\s*$/i, '').replace(/[₺€$%]/g, '').trim();

        // Sayı formatını tespit et ve parse et
        let numValue;
        let parseMethod = '';

        // Format 1: "1,882.13" (İngilizce - virgül binlik, nokta ondalık)
        if (/^[\d,]+\.\d+$/.test(cleanValue)) {
          numValue = parseFloat(cleanValue.replace(/,/g, ''));
          parseMethod = 'EN with decimal';
        }
        // Format 2: "651,73" veya "1.234,56" (Türkçe - virgül ondalık)
        // Virgülden sonra 1-2 rakam varsa Türkçe ondalık
        else if (/^[\d.]*,\d{1,2}$/.test(cleanValue)) {
          numValue = parseFloat(cleanValue.replace(/\./g, '').replace(',', '.'));
          parseMethod = 'TR decimal';
        }
        // Format 3: "1,234,567" (İngilizce - virgül binlik, ondalık yok)
        // Virgülden sonra 3 rakam varsa İngilizce binlik ayraç
        else if (/^\d{1,3}(,\d{3})+$/.test(cleanValue)) {
          numValue = parseFloat(cleanValue.replace(/,/g, ''));
          parseMethod = 'EN thousands';
        }
        // Format 4: "1.234" veya "1.234.567" (Türkçe - nokta binlik, ondalık yok)
        else if (/^\d{1,3}(\.\d{3})+$/.test(cleanValue)) {
          numValue = parseFloat(cleanValue.replace(/\./g, ''));
          parseMethod = 'TR thousands';
        }
        // Format 5: Basit sayı "1882.13" veya "1882" veya "1882,5"
        else {
          numValue = parseFloat(cleanValue.replace(',', '.'));
          parseMethod = 'Simple';
        }

        // Debug log
        // console.log(`Parse [${parseMethod}]: "${value}" -> "${cleanValue}" -> ${numValue}`);

        obj[header] = isNaN(numValue) ? cleanValue : numValue;
        obj[`${header}_display`] = value;
        obj[`${header}_raw`] = String(cleanValue).toUpperCase();
      }
    });
    return obj;
  });

  const firstHeader = headers[0];

  // Gruplu tablolarda boş ilk kolon değerlerini yukarıdan doldur (sıralamadan ÖNCE)
  // Örnek: OCAK satırından sonra gelen boş Ay hücreleri OCAK ile doldurulur
  data.forEach((row, i) => {
    if (i > 0 && (!row[firstHeader] || String(row[firstHeader]).trim() === '')) {
      row[firstHeader] = data[i - 1][firstHeader];
    }
  });

  // Tarih sıralaması kontrolü
  const hasDateData = data.some(row => isDateString(String(row[firstHeader])));

  if (hasDateData) {
    data = data.sort((a, b) => {
      const aDate = parseDate(String(a[firstHeader]));
      const bDate = parseDate(String(b[firstHeader]));
      return aDate - bDate;
    });
  } else {
    // Ay sıralaması kontrolü - ilk kolonda ay ismi varsa sırala
    const hasMonthData = data.some(row => {
      const val = String(row[firstHeader]).toUpperCase();
      return MONTH_ORDER[val] !== undefined;
    });

    if (hasMonthData) {
      data = data.sort((a, b) => {
        const aMonth = String(a[firstHeader]).toUpperCase();
        const bMonth = String(b[firstHeader]).toUpperCase();
        return (MONTH_ORDER[aMonth] || 99) - (MONTH_ORDER[bMonth] || 99);
      });
    }
  }

  return { headers, data };
};

// Tablonun üstündeki başlık metnini temizle (numaralandırma, markdown işaretleri vb.)
const cleanTableTitle = (text) => {
  if (!text) return '';
  return text
    .replace(/^#+\s*/, '')
    .replace(/^\d+[.)]\s*/, '')
    .replace(/\*\*/g, '')
    .replace(/:\s*$/, '')
    .trim();
};

// Tüm markdown tablolarını ayrı ayrı parse et (birden fazla tablo varsa karışmasını önler)
const parseAllMarkdownTables = (content) => {
  const lines = content.split('\n');
  const tableGroups = [];
  let currentGroup = [];
  let lastText = '';

  for (const line of lines) {
    if (line.trim().startsWith('|')) {
      currentGroup.push(line);
    } else {
      if (currentGroup.length >= 3) {
        tableGroups.push({ text: currentGroup.join('\n'), title: cleanTableTitle(lastText) });
      }
      currentGroup = [];
      if (line.trim()) lastText = line.trim();
    }
  }
  if (currentGroup.length >= 3) {
    tableGroups.push({ text: currentGroup.join('\n'), title: cleanTableTitle(lastText) });
  }

  return tableGroups.map(group => {
    const parsed = parseMarkdownTable(group.text);
    return parsed ? { ...parsed, title: group.title } : null;
  }).filter(Boolean);
};

// React children'dan text çıkar
const extractText = (el) => {
  if (typeof el === 'string') return el;
  if (typeof el === 'number') return String(el);
  if (Array.isArray(el)) return el.map(extractText).join('');
  if (el?.props?.children) return extractText(el.props.children);
  return '';
};

// Grafiğe uygun veri mi kontrol et
const MAX_CHARTABLE_ROWS = 12;

const isChartableData = (tableData, query) => {
  if (!tableData || tableData.data.length < 2) return false;
  if (tableData.headers.length < 2) return false;

  // "Versiyon" ile ilgili sorularda grafik anlamlı olmuyor (çok fazla satır oluyor)
  const queryLower = (query || '').toLowerCase();
  const firstHeaderLower = tableData.headers[0].toLowerCase();
  if (queryLower.includes('versiyon') || firstHeaderLower.includes('versiyon')) return false;

  // Satır sayısı çok fazlaysa grafik anlamlı olmuyor
  if (tableData.data.length > MAX_CHARTABLE_ROWS) return false;

  // En az bir sayısal kolon olmalı
  const hasNumeric = tableData.headers.some(header => {
    return tableData.data.some(row => typeof row[header] === 'number');
  });

  return hasNumeric;
};

// Grafik türünü belirle
const determineChartType = (tableData, query) => {
  const queryLower = query.toLowerCase();
  
  // Kullanıcı isteğine göre (6'dan fazla kategori varsa pie yerine bar)
  if (queryLower.includes('pasta') || queryLower.includes('pie') || queryLower.includes('dağılım')) {
    const rowCount = tableData.data.filter(row => {
      const totalKeywords = ['genel toplam', 'toplam', 'grand total', 'total', 'genel', 'ara toplam'];
      const firstVal = String(row[tableData.headers[0]] || '').toLowerCase().trim().replace(/\*/g, '');
      return !totalKeywords.some(kw => firstVal === kw) && firstVal !== 'kullanım yok';
    }).length;
    return rowCount <= 6 ? 'pie' : 'bar';
  }
  if (queryLower.includes('çizgi') || queryLower.includes('line') || queryLower.includes('trend')) {
    return 'line';
  }
  
  // Zaman serisi verileri için çizgi grafik
  const timeKeywords = ['ay', 'yıl', 'hafta', 'gün', 'tarih', 'ocak', 'şubat', 'mart', 'nisan', 'mayıs', 'haziran', 'temmuz', 'ağustos', 'eylül', 'ekim', 'kasım', 'aralık'];
  const firstHeader = tableData.headers[0].toLowerCase();
  if (timeKeywords.some(kw => firstHeader.includes(kw))) {
    return 'line';
  }
  
  // Varsayılan bar grafik
  return 'bar';
};

// Tablo başlığından kısa bir metrik adı çıkar (uzun cümle yerine tek kelime/kısa etiket)
const METRIC_KEYWORDS = [
  { pattern: /bütçe|harcama/i, label: 'Bütçe' },
  { pattern: /\bsov\b/i, label: 'SOV' },
  { pattern: /\bsos\b/i, label: 'SOS' },
  { pattern: /\bgrp\b/i, label: 'GRP' },
  { pattern: /süre/i, label: 'Süre' },
  { pattern: /oran|\bpay\b/i, label: 'Pay/Oran' },
];

const extractMetricKeyword = (text) => {
  if (!text) return '';
  const match = METRIC_KEYWORDS.find(({ pattern }) => pattern.test(text));
  return match ? match.label : '';
};

// Geniş format kolon adından ("Getir Bütçe (₺)") sadece marka adını çıkar ("Getir")
// - metrik adı zaten grafik başlığında yazdığı için serilerde tekrar etmesin
const cleanSeriesLabel = (label) => {
  const cleaned = String(label)
    .replace(/\(.*?\)/g, '')
    .replace(/\b(bütçe|harcama|sos|sov|grp|süre|oran|pay)\b/gi, '')
    .replace(/\s+/g, ' ')
    .trim();
  return cleaned || label;
};

// Sayı formatlama
const formatNumber = (value) => {
  if (typeof value !== 'number') return value;
  if (value >= 1000000) {
    return (value / 1000000).toFixed(1) + 'M';
  }
  if (value >= 1000) {
    return (value / 1000).toFixed(1) + 'K';
  }
  return value.toFixed(1);
};

// Grafik bileşeni
const DataChart = ({ tableData, chartType, metricLabel }) => {
  if (!tableData || tableData.data.length === 0) return null;

  const xKey = tableData.headers[0];

  // Toplam/özet satırlarını grafik verisinden çıkar (tabloda kalır, grafikte gösterilmez)
  const totalKeywords = ['genel toplam', 'toplam', 'grand total', 'total', 'genel', 'ara toplam'];
  const filteredData = tableData.data.filter(row => {
    // Tüm sütunlarda toplam/özet kontrolü
    for (const h of tableData.headers) {
      const val = String(row[h] || '').toLowerCase().trim().replace(/\*/g, '');
      if (totalKeywords.some(kw => val === kw)) return false;
      if (val === 'kullanım yok') return false;
    }
    return true;
  });
  // Sayısal sütunları bul (toplam kolonunu hariç tut)
  const totalColKeywords = ['toplam', 'total', 'grand total', 'genel toplam'];
  const allNumericCols = tableData.headers.filter((h, i) => {
    if (i === 0) return false;
    const hLower = h.toLowerCase();
    if (totalColKeywords.some(kw => hLower.includes(kw))) return false;
    return filteredData.some(row => typeof row[h] === 'number');
  });
  // Yüzde/oran tipi kolonları (SOV, SOS, oran, %) tutar bazlı kolonlarla aynı grafikte karıştırma:
  // farklı birimler (₺ vs %) aynı çizgi/pasta grafiğe girmesin diye tutar bazlı kolonlar önceliklidir.
  // Tabloda sadece yüzde tipi kolon varsa (tutar kolonu yoksa) onlar kullanılır.
  const percentColKeywords = ['sov', 'sos', 'oran'];
  const isPercentCol = (h) => h.includes('%') || percentColKeywords.some(kw => h.toLowerCase().includes(kw));
  const absoluteCols = allNumericCols.filter(h => !isPercentCol(h));
  const numericCols = absoluteCols.length > 0 ? absoluteCols : allNumericCols;
  const yKey = numericCols[0] || tableData.headers[1];
  // Grafik başlığında gösterilecek kısa metrik adı: başlık metninden anahtar kelime çıkar,
  // bulunamazsa sütun adına düş (geniş formatta ilk sütun marka adı olabileceğinden yKey güvenilir değildir)
  const metricKeyword = extractMetricKeyword(metricLabel);
  const metricName = metricKeyword || yKey;

  // Boş ilk kolon değerlerini yukarıdan doldur (gruplu tablolarda ay bir kez yazılır)
  const filledData = filteredData.map((row, i, arr) => {
    if (!row[xKey] || String(row[xKey]).trim() === '') {
      for (let j = i - 1; j >= 0; j--) {
        if (arr[j][xKey] && String(arr[j][xKey]).trim() !== '') {
          return { ...row, [xKey]: arr[j][xKey] };
        }
      }
    }
    return row;
  });

  // Çok boyutlu veri tespiti (Ay + Marka gibi 2 kategorik kolon)
  const groupKey = tableData.headers[1];
  const isMultiDim = (() => {
    if (tableData.headers.length < 3) return false;
    // İlk iki kolon kategorik (string) olmalı
    const col1NonNumeric = filledData.filter(row => typeof row[xKey] === 'string' && row[xKey]).length > filledData.length * 0.5;
    const col2NonNumeric = filledData.filter(row => typeof row[groupKey] === 'string' && row[groupKey]).length > filledData.length * 0.5;
    if (!col1NonNumeric || !col2NonNumeric) return false;
    // İlk kolonda tekrar eden değerler olmalı (gruplu veri)
    const xValues = filledData.map(row => String(row[xKey]).trim()).filter(v => v);
    const uniqueX = new Set(xValues);
    return uniqueX.size < xValues.length * 0.7;
  })();

  // Geniş (wide) format: her marka/kırılım zaten ayrı bir sütun (Ay, Getir10, GetirBüyük, ...)
  const isWideMultiSeries = !isMultiDim && numericCols.length > 1;

  // Tek seri tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="chart-tooltip">
          <p className="tooltip-label">{label}</p>
          <p className="tooltip-value">{typeof payload[0].value === 'number' ? payload[0].value.toLocaleString('tr-TR') : payload[0].value}</p>
        </div>
      );
    }
    return null;
  };

  // Çok seri tooltip
  const MultiSeriesTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="chart-tooltip">
          <p className="tooltip-label" style={{ marginBottom: 8, fontWeight: 'bold' }}>{label}</p>
          {payload.filter(p => p.value > 0).map((entry, i) => (
            <p key={i} style={{ color: entry.color, margin: '3px 0', fontSize: 13 }}>
              {entry.name}: {entry.value?.toLocaleString('tr-TR')}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Pie/tekil bar tooltip (Recharts varsayılanı yerine okunabilir tema)
  const SlicetTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const entry = payload[0];
      return (
        <div className="chart-tooltip">
          <p className="tooltip-label" style={{ color: entry.color || entry.payload?.fill }}>{entry.name}</p>
          <p className="tooltip-value">{typeof entry.value === 'number' ? entry.value.toLocaleString('tr-TR') : entry.value}</p>
        </div>
      );
    }
    return null;
  };

  // X ekseni için tarih/ay formatla
  const formatXAxis = (value) => {
    if (!value) return '';
    const strValue = String(value);

    if (/^\d{4}-\d{2}-\d{2}/.test(strValue)) {
      const match = strValue.match(/^\d{4}-(\d{2})-(\d{2})/);
      if (match) return `${match[2]}/${match[1]}`;
    }
    if (/^\d{2}\.\d{2}\.\d{4}/.test(strValue)) {
      const match = strValue.match(/^(\d{2})\.(\d{2})\./);
      if (match) return `${match[1]}/${match[2]}`;
    }
    if (strValue.includes('2024') || strValue.includes('2023') || strValue.includes('2025')) {
      const dateMatch = strValue.match(/(\d{2})-(\d{2})(?:T|$|\s)/);
      if (dateMatch) return `${dateMatch[2]}/${dateMatch[1]}`;
      const simpleMatch = strValue.match(/-(\d{2})-(\d{2})/);
      if (simpleMatch) return `${simpleMatch[2]}/${simpleMatch[1]}`;
    }
    const upperValue = strValue.toUpperCase();
    const monthMap = {
      'OCAK': 'Oca', 'ŞUBAT': 'Şub', 'MART': 'Mar', 'NİSAN': 'Nis',
      'MAYIS': 'May', 'HAZİRAN': 'Haz', 'TEMMUZ': 'Tem', 'AĞUSTOS': 'Ağu',
      'EYLÜL': 'Eyl', 'EKİM': 'Eki', 'KASIM': 'Kas', 'ARALIK': 'Ara'
    };
    if (monthMap[upperValue]) return monthMap[upperValue];
    if (!isNaN(value)) return value;
    if (strValue.length > 20) return strValue.substring(0, 12) + '...';
    return strValue;
  };

  // === ÇOK BOYUTLU VERİ: Stacked Bar Chart + Özet Pie Chart ===
  if (isMultiDim || isWideMultiSeries) {
    let groups, pivotedData, groupTotals;

    if (isMultiDim) {
      // Grup ve X değerlerini çıkar
      groups = [...new Set(filledData.map(row => row[groupKey]).filter(Boolean))];
      const xValues = [...new Set(filledData.map(row => row[xKey]).filter(Boolean))];

      // X değerlerini ay sırasına göre sırala
      xValues.sort((a, b) => {
        const aOrder = MONTH_ORDER[String(a).toUpperCase()] || 99;
        const bOrder = MONTH_ORDER[String(b).toUpperCase()] || 99;
        return aOrder - bOrder;
      });

      // Pivot: her X değeri için grupları sütun yap
      pivotedData = xValues.map(x => {
        const obj = { [xKey]: x };
        groups.forEach(group => {
          const match = filledData.find(row => row[xKey] === x && row[groupKey] === group);
          obj[group] = match ? (typeof match[yKey] === 'number' ? match[yKey] : 0) : 0;
        });
        return obj;
      });

      // Grup bazında toplam (pie chart için)
      groupTotals = groups.map(group => ({
        name: group,
        value: filledData
          .filter(row => row[groupKey] === group)
          .reduce((sum, row) => sum + (typeof row[yKey] === 'number' ? row[yKey] : 0), 0)
      })).filter(g => g.value > 0).sort((a, b) => b.value - a.value);
    } else {
      // Geniş format: her sayısal sütun zaten ayrı bir marka/kırılım serisi
      groups = numericCols;

      // X değerlerini ay sırasına göre sırala
      pivotedData = [...filledData].sort((a, b) => {
        const aOrder = MONTH_ORDER[String(a[xKey]).toUpperCase()] || 99;
        const bOrder = MONTH_ORDER[String(b[xKey]).toUpperCase()] || 99;
        return aOrder - bOrder;
      });

      // Kolon bazında toplam (pie chart için)
      groupTotals = groups.map(group => ({
        name: cleanSeriesLabel(group),
        value: filteredData.reduce((sum, row) => sum + (typeof row[group] === 'number' ? row[group] : 0), 0)
      })).filter(g => g.value > 0).sort((a, b) => b.value - a.value);
    }

    const dimensionLabel = isMultiDim ? groupKey : 'Marka/Kırılım';
    // isWideMultiSeries durumunda yKey gerçek bir metrik değil, ilk marka sütununun adıdır -
    // bu yüzden anahtar kelime bulunamazsa markaya değil jenerik "Değer" etiketine düş
    const comboMetricName = metricKeyword || (isMultiDim ? yKey : 'Değer');
    const seriesLabel = (group) => (isMultiDim ? group : cleanSeriesLabel(group));

    return (
      <div>
        {/* Çoklu Çizgi Grafik - Ay bazında marka kırılımı */}
        <div className="chart-container">
          <p className="chart-title">{comboMetricName} — Aylık Trend ({dimensionLabel} Bazlı)</p>
          <ResponsiveContainer width="100%" height={500}>
            <LineChart data={pivotedData} margin={{ top: 20, right: 30, left: 20, bottom: 100 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
              <XAxis
                dataKey={xKey}
                stroke="#94a3b8"
                tick={{ fill: '#94a3b8', fontSize: 11 }}
                angle={-45}
                textAnchor="end"
                height={100}
                interval={0}
                tickFormatter={formatXAxis}
              />
              <YAxis stroke="#94a3b8" tick={{ fill: '#94a3b8' }} tickFormatter={formatNumber} />
              <Tooltip content={<MultiSeriesTooltip />} />
              <Legend wrapperStyle={{ paddingTop: '20px' }} />
              {groups.map((group, i) => (
                <Line
                  key={group}
                  type="monotone"
                  dataKey={group}
                  name={seriesLabel(group)}
                  stroke={MULTI_SERIES_COLORS[i % MULTI_SERIES_COLORS.length]}
                  strokeWidth={3}
                  dot={{ fill: MULTI_SERIES_COLORS[i % MULTI_SERIES_COLORS.length], strokeWidth: 2, r: 4 }}
                  activeDot={{ r: 7 }}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Özet Grafik - Toplam bazında dağılım (6+ grup → bar chart) */}
        <div className="chart-container">
          <p className="chart-title">{comboMetricName} — Toplam Dağılım ({dimensionLabel} Bazlı)</p>
          <ResponsiveContainer width="100%" height={400}>
            {groupTotals.length <= 6 ? (
              <PieChart>
                <Pie
                  data={groupTotals}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={130}
                  paddingAngle={2}
                  label={({ name, percent }) => `${name} (${(percent * 100).toFixed(1)}%)`}
                  labelLine={{ stroke: '#94a3b8', strokeWidth: 1 }}
                >
                  {groupTotals.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={MULTI_SERIES_COLORS[index % MULTI_SERIES_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip content={<SlicetTooltip />} />
                <Legend />
              </PieChart>
            ) : (
              <BarChart data={groupTotals} margin={{ top: 20, right: 30, left: 20, bottom: 100 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                <XAxis dataKey="name" stroke="#94a3b8" tick={{ fill: '#94a3b8', fontSize: 11 }} angle={-45} textAnchor="end" height={100} interval={0} tickFormatter={formatXAxis} />
                <YAxis stroke="#94a3b8" tick={{ fill: '#94a3b8' }} tickFormatter={formatNumber} />
                <Tooltip content={<SlicetTooltip />} />
                <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                  {groupTotals.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={MULTI_SERIES_COLORS[index % MULTI_SERIES_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            )}
          </ResponsiveContainer>
        </div>
      </div>
    );
  }

  // === TEK BOYUTLU VERİ ===

  if (chartType === 'pie') {
    const pieData = filledData.map(row => ({
      name: String(row[xKey] || ''),
      value: typeof row[yKey] === 'number' ? row[yKey] : 0
    })).filter(d => d.value > 0);

    // 6'dan fazla kategori varsa bar chart kullan
    if (pieData.length > 6) {
      return (
        <div className="chart-container">
          <p className="chart-title">{xKey} Bazlı {metricName} Dağılımı</p>
          <ResponsiveContainer width="100%" height={500}>
            <BarChart data={pieData} margin={{ top: 20, right: 30, left: 20, bottom: 100 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
              <XAxis dataKey="name" stroke="#94a3b8" tick={{ fill: '#94a3b8', fontSize: 11 }} angle={-45} textAnchor="end" height={100} interval={0} tickFormatter={formatXAxis} />
              <YAxis stroke="#94a3b8" tick={{ fill: '#94a3b8' }} tickFormatter={formatNumber} />
              <Tooltip content={<SlicetTooltip />} />
              <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      );
    }

    return (
      <div className="chart-container">
        <p className="chart-title">{metricName} Dağılımı</p>
        <ResponsiveContainer width="100%" height={450}>
          <PieChart>
            <Pie
              data={pieData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius={60}
              outerRadius={140}
              paddingAngle={2}
              label={({ name, percent }) => `${name} (${(percent * 100).toFixed(1)}%)`}
              labelLine={{ stroke: '#94a3b8', strokeWidth: 1 }}
            >
              {pieData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip content={<SlicetTooltip />} />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      </div>
    );
  }

  if (chartType === 'line') {
    return (
      <div className="chart-container">
        <p className="chart-title">{xKey} Bazlı {metricName} Trendi</p>
        <ResponsiveContainer width="100%" height={500}>
          <LineChart data={filledData} margin={{ top: 20, right: 30, left: 20, bottom: 100 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
            <XAxis
              dataKey={xKey}
              stroke="#94a3b8"
              tick={{ fill: '#94a3b8', fontSize: 10 }}
              angle={-45}
              textAnchor="end"
              height={100}
              interval={0}
              tickFormatter={formatXAxis}
            />
            <YAxis stroke="#94a3b8" tick={{ fill: '#94a3b8' }} tickFormatter={formatNumber} />
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ paddingTop: '20px' }} />
            <Line
              type="monotone"
              dataKey={yKey}
              stroke="#a855f7"
              strokeWidth={3}
              dot={{ fill: '#a855f7', strokeWidth: 2, r: 4 }}
              activeDot={{ r: 7, fill: '#c084fc' }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  }

  // Bar chart (default)
  return (
    <div className="chart-container">
      <p className="chart-title">{xKey} Bazlı {metricName}</p>
      <ResponsiveContainer width="100%" height={500}>
        <BarChart data={filledData} margin={{ top: 20, right: 30, left: 20, bottom: 100 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
          <XAxis
            dataKey={xKey}
            stroke="#94a3b8"
            tick={{ fill: '#94a3b8', fontSize: 10 }}
            angle={-45}
            textAnchor="end"
            height={100}
            interval={0}
            tickFormatter={formatXAxis}
          />
          <YAxis stroke="#94a3b8" tick={{ fill: '#94a3b8' }} tickFormatter={formatNumber} />
          <Tooltip content={<CustomTooltip />} />
          <Legend
            wrapperStyle={{ paddingTop: '20px' }}
            payload={filledData.map((entry, index) => ({
              value: String(entry[xKey] || ''),
              type: 'rect',
              color: COLORS[index % COLORS.length]
            }))}
          />
          <Bar dataKey={yKey} radius={[6, 6, 0, 0]} minPointSize={5}>
            {filledData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

// Looker Link bileşeni - SSO URL ile açar
const LookerLink = ({ href, children }) => {
  const [loading, setLoading] = useState(false);

  const handleClick = async (e) => {
    e.preventDefault();

    // Looker URL'i mi kontrol et
    const dashboardMatch = href.match(/\/dashboards\/(\d+)/);
    const lookMatch = href.match(/\/looks\/(\d+)/);

    if (!dashboardMatch && !lookMatch) {
      // Looker URL değilse normal aç
      window.open(href, '_blank');
      return;
    }

    setLoading(true);

    try {
      const type = dashboardMatch ? 'dashboard' : 'look';
      const id = dashboardMatch ? dashboardMatch[1] : lookMatch[1];

      const response = await axios.post(`${API_URL}/api/embed-url`, { type, id });

      if (response.data.success) {
        window.open(response.data.embed_url, '_blank');
      } else {
        // Hata durumunda normal URL aç
        window.open(href, '_blank');
      }
    } catch (error) {
      console.error('SSO URL error:', error);
      window.open(href, '_blank');
    } finally {
      setLoading(false);
    }
  };

  return (
    <a
      href={href}
      onClick={handleClick}
      className={`looker-link ${loading ? 'loading' : ''}`}
      title="SSO ile aç"
    >
      {loading ? '⏳ ' : ''}{children}
    </a>
  );
};

// Mesaj bileşeni
const MessageContent = ({ content, userQuery }) => {
  const allTables = parseAllMarkdownTables(content);
  let tableIdx = 0;

  // En iyi tabloyu grafik için seç (en çok veri satırı olan)
  const bestTableForChart = allTables.reduce((best, table) => {
    if (!isChartableData(table, userQuery)) return best;
    if (!best) return table;
    const bestScore = best.data.length * best.headers.filter((h, i) => i > 0 && best.data.some(r => typeof r[h] === 'number')).length;
    const tableScore = table.data.length * table.headers.filter((h, i) => i > 0 && table.data.some(r => typeof r[h] === 'number')).length;
    return tableScore > bestScore ? table : best;
  }, null);

  const chartType = bestTableForChart ? determineChartType(bestTableForChart, userQuery || '') : null;

  // Looker URL'lerini bul
  const lookerUrlRegex = /(https:\/\/[a-zA-Z0-9.-]+\.looker\.com\/(?:dashboards|looks|explore)\/[^\s\)]+)/g;
  const lookerUrls = content.match(lookerUrlRegex) || [];

  const totalKeywords = ['toplam', 'total', 'grand total', 'genel toplam', 'ara toplam'];

  return (
    <div className="message-content">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          thead: ({ children }) => <thead>{children}</thead>,
          tbody: ({ children }) => <tbody>{children}</tbody>,
          th: ({ children }) => <th>{children}</th>,
          table: ({ children }) => {
            const currentTableData = allTables[tableIdx++] || null;
            return (
              <div className="table-container">
                {currentTableData && (
                  <div className="table-toolbar">
                    <button className="export-btn" onClick={() => exportToExcel(currentTableData)}>
                       Excel'e Aktar
                    </button>
                  </div>
                )}
                <table>{children}</table>
              </div>
            );
          },
          tr: ({ children }) => {
            let rowText = '';
            React.Children.forEach(children, child => {
              rowText += ' ' + extractText(child?.props?.children);
            });
            const isTotal = totalKeywords.some(kw => rowText.toLowerCase().includes(kw));
            return <tr className={isTotal ? 'total-row' : ''}>{children}</tr>;
          },
          td: ({ children }) => {
            const text = extractText(children).trim();
            const isNumeric = /\d/.test(text) && /^[%₺$€]?[\d.,\s]+[%₺$€TL]*$/.test(text.replace(/\*\*/g, ''));
            return <td className={isNumeric ? 'numeric-cell' : ''}>{children}</td>;
          },
          a: ({ href, children }) => {
            const isLookerUrl = href && href.includes('.looker.com/');
            if (isLookerUrl) {
              return <LookerLink href={href}>{children}</LookerLink>;
            }
            return (
              <a href={href} target="_blank" rel="noopener noreferrer">
                {children}
              </a>
            );
          }
        }}
      >
        {content}
      </ReactMarkdown>

      {bestTableForChart && chartType && (
        <DataChart tableData={bestTableForChart} chartType={chartType} metricLabel={bestTableForChart.title} />
      )}

      {lookerUrls.length > 0 && (
        <div className="looker-embed-container">
          {lookerUrls.map((url, index) => (
            <LookerEmbed key={index} url={url} />
          ))}
        </div>
      )}
    </div>
  );
};

// Looker Embed bileşeni
const LookerEmbed = ({ url }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Preview için state
  const [previewLoading, setPreviewLoading] = useState(true);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewError, setPreviewError] = useState(null);

  // Fullscreen için state
  const [fullscreenLoading, setFullscreenLoading] = useState(true);
  const [fullscreenUrl, setFullscreenUrl] = useState(null);
  const [fullscreenError, setFullscreenError] = useState(null);

  // Dashboard ID'yi URL'den çıkar
  const getDashboardInfo = () => {
    const dashboardMatch = url.match(/\/dashboards\/(\d+)/);
    const lookMatch = url.match(/\/looks\/(\d+)/);

    if (dashboardMatch) {
      return { type: 'dashboard', id: dashboardMatch[1] };
    } else if (lookMatch) {
      return { type: 'look', id: lookMatch[1] };
    }
    return null;
  };

  // Preview için embed URL al (component mount olduğunda)
  useEffect(() => {
    const getPreviewUrl = async () => {
      try {
        const info = getDashboardInfo();
        if (!info) {
          throw new Error('Geçersiz Looker URL');
        }

        const response = await axios.post(`${API_URL}/api/embed-url`, {
          type: info.type,
          id: info.id
        });

        if (response.data.success) {
          setPreviewUrl(response.data.embed_url);
        } else {
          throw new Error(response.data.error);
        }
      } catch (error) {
        console.error('Preview URL error:', error);
        setPreviewError(error.message);
        setPreviewUrl(url.replace(/\/(dashboards|looks|explore)\//, '/embed/$1/'));
      }
    };

    getPreviewUrl();
  }, [url]);

  // Fullscreen için embed URL al (açıldığında)
  useEffect(() => {
    if (!isExpanded) {
      setFullscreenUrl(null);
      setFullscreenLoading(true);
      setFullscreenError(null);
      return;
    }

    const getFullscreenUrl = async () => {
      try {
        const info = getDashboardInfo();
        if (!info) {
          throw new Error('Geçersiz Looker URL');
        }

        const response = await axios.post(`${API_URL}/api/embed-url`, {
          type: info.type,
          id: info.id
        });

        if (response.data.success) {
          setFullscreenUrl(response.data.embed_url);
        } else {
          throw new Error(response.data.error);
        }
      } catch (error) {
        console.error('Fullscreen URL error:', error);
        setFullscreenError(error.message);
        setFullscreenUrl(url.replace(/\/(dashboards|looks|explore)\//, '/embed/$1/'));
      }
    };

    getFullscreenUrl();
  }, [isExpanded, url]);

  // ESC tuşu ile kapat
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && isExpanded) {
        setIsExpanded(false);
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isExpanded]);

  // Genişletildiğinde scroll'u kapat
  useEffect(() => {
    if (isExpanded) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isExpanded]);

  // Preview görünümü
  const previewContent = (
    <div className="looker-embed">
      <div className="embed-header">
        <span className="embed-title">📊 Dashboard Önizleme</span>
        <div className="embed-actions">
          <button
            className="embed-btn"
            onClick={() => setIsExpanded(true)}
          >
            ⛶ Tam Ekran
          </button>
        </div>
      </div>

      <div className="embed-content">
        {previewLoading && (
          <div className="embed-loading">
            <div className="loading-dots">
              <span></span>
              <span></span>
              <span></span>
            </div>
            <span>Dashboard yükleniyor...</span>
          </div>
        )}
        {previewError && (
          <div className="embed-error">
            ⚠️ Embed yüklenemedi. <a href={url} target="_blank" rel="noopener noreferrer">Looker'da aç</a>
          </div>
        )}
        {previewUrl && (
          <iframe
            src={previewUrl}
            title="Looker Dashboard Preview"
            onLoad={() => setPreviewLoading(false)}
            style={{ display: previewLoading ? 'none' : 'block' }}
            allow="fullscreen"
          />
        )}
      </div>
    </div>
  );

  // Tam ekran modal
  const fullscreenModal = isExpanded ? ReactDOM.createPortal(
    <>
      <div className="embed-overlay" onClick={() => setIsExpanded(false)} />
      <div className="looker-embed expanded">
        <div className="embed-header">
          <span className="embed-title">📊 Dashboard Önizleme</span>
          <div className="embed-actions">
            <button
              className="embed-btn"
              onClick={() => setIsExpanded(false)}
            >
              ✕ Kapat
            </button>
          </div>
        </div>

        <div className="embed-content">
          {fullscreenLoading && (
            <div className="embed-loading">
              <div className="loading-dots">
                <span></span>
                <span></span>
                <span></span>
              </div>
              <span>Dashboard yükleniyor...</span>
            </div>
          )}
          {fullscreenError && (
            <div className="embed-error">
              ⚠️ Embed yüklenemedi. <a href={url} target="_blank" rel="noopener noreferrer">Looker'da aç</a>
            </div>
          )}
          {fullscreenUrl && (
            <iframe
              src={fullscreenUrl}
              title="Looker Dashboard Fullscreen"
              onLoad={() => setFullscreenLoading(false)}
              style={{ display: fullscreenLoading ? 'none' : 'block' }}
              allow="fullscreen"
            />
          )}
        </div>
      </div>
    </>,
    document.body
  ) : null;

  return (
    <>
      {previewContent}
      {fullscreenModal}
    </>
  );
};

function App() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingSteps, setLoadingSteps] = useState([]);
  const [connected, setConnected] = useState(null);
  const [savedDashboards, setSavedDashboards] = useState([]);
  const [showDashboardPanel, setShowDashboardPanel] = useState(false);
  const [selectedDashboard, setSelectedDashboard] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [editingDashboardId, setEditingDashboardId] = useState(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [activeCategoryPopup, setActiveCategoryPopup] = useState(null);
  const [authUser, setAuthUser] = useState(null);
  const [authChecking, setAuthChecking] = useState(true);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [guestMode, setGuestMode] = useState(() => localStorage.getItem('guestMode') === 'true');
  const [conversations, setConversations] = useState([]);
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(() => window.innerWidth > 768);
  const messagesEndRef = useRef(null);

  // Kaydedilmiş dashboard'ları localStorage'dan yükle
  useEffect(() => {
    const saved = localStorage.getItem('savedDashboards');
    if (saved) {
      setSavedDashboards(JSON.parse(saved));
    }
  }, []);

  // Okta girişi şimdilik devre dışı (e-posta/şifre girişine geçildi)
  // useEffect(() => {
  //   const initOkta = async () => {
  //     try {
  //       if (oktaAuth.isLoginRedirect()) {
  //         await oktaAuth.handleLoginRedirect();
  //       }
  //       const isAuthenticated = await oktaAuth.isAuthenticated();
  //       if (isAuthenticated) {
  //         const user = await oktaAuth.getUser();
  //         setOktaUser(user);
  //       }
  //     } catch (error) {
  //       console.error('Okta giriş kontrolü başarısız:', error);
  //     } finally {
  //       setAuthChecking(false);
  //     }
  //   };
  //   initOkta();
  // }, []);

  // E-posta/şifre ile daha önce giriş yapılmışsa localStorage'daki oturumu geri yükle
  useEffect(() => {
    const storedToken = localStorage.getItem('authToken');
    const storedUser = localStorage.getItem('authUser');
    if (storedToken && storedUser) {
      try {
        setAuthUser(JSON.parse(storedUser));
      } catch (error) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('authUser');
      }
    }
    setAuthChecking(false);
  }, []);

  // Kullanıcı giriş yaptığında sohbet geçmişini yükle
  useEffect(() => {
    if (authUser) fetchConversations();
  }, [authUser]);

  const fetchConversations = async () => {
    const idToken = localStorage.getItem('authToken');
    if (!idToken) return;
    try {
      const response = await axios.get(`${API_URL}/api/conversations`, {
        headers: { Authorization: `Bearer ${idToken}` }
      });
      if (response.data.success) setConversations(response.data.conversations);
    } catch (error) {
      console.error('Konuşma listesi alınamadı:', error);
    }
  };

  const startNewConversation = () => {
    setMessages([]);
    setActiveConversationId(null);
    if (window.innerWidth <= 768) setSidebarOpen(false);
  };

  const loadConversation = async (id) => {
    if (id === activeConversationId || loading) return;
    try {
      const idToken = localStorage.getItem('authToken');
      const response = await axios.get(`${API_URL}/api/conversations/${id}`, {
        headers: { Authorization: `Bearer ${idToken}` }
      });
      if (response.data.success) {
        const conv = response.data.conversation;
        setMessages(conv.messages.map(m => ({ role: m.role, content: m.content, userQuery: m.userQuery })));
        setActiveConversationId(id);
        if (window.innerWidth <= 768) setSidebarOpen(false);
      }
    } catch (error) {
      console.error('Konuşma yüklenemedi:', error);
    }
  };

  const deleteConversation = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Bu konuşmayı silmek istediğinize emin misiniz?')) return;
    try {
      const idToken = localStorage.getItem('authToken');
      await axios.delete(`${API_URL}/api/conversations/${id}`, {
        headers: { Authorization: `Bearer ${idToken}` }
      });
      setConversations(prev => prev.filter(c => c._id !== id));
      if (id === activeConversationId) startNewConversation();
    } catch (error) {
      console.error('Konuşma silinemedi:', error);
    }
  };

  // const handleOktaLogin = () => {
  //   oktaAuth.signInWithRedirect();
  // };

  // const handleOktaLogout = async () => {
  //   await oktaAuth.signOut();
  //   setOktaUser(null);
  //   localStorage.removeItem('guestMode');
  //   setGuestMode(false);
  //   setConversations([]);
  //   setActiveConversationId(null);
  //   setMessages([]);
  // };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoginLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/login`, {
        email: loginEmail,
        password: loginPassword,
      });
      if (response.data.success) {
        localStorage.setItem('authToken', response.data.token);
        localStorage.setItem('authUser', JSON.stringify(response.data.user));
        setAuthUser(response.data.user);
        setLoginPassword('');
      }
    } catch (error) {
      setLoginError(error.response?.data?.error || 'Giriş başarısız');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('authUser');
    setAuthUser(null);
    localStorage.removeItem('guestMode');
    setGuestMode(false);
    setConversations([]);
    setActiveConversationId(null);
    setMessages([]);
  };

  const continueAsGuest = () => {
    localStorage.setItem('guestMode', 'true');
    setGuestMode(true);
  };

  // Dashboard kaydet
  const saveDashboard = (id, title) => {
    const newDashboard = {
      id,
      title,
      createdAt: new Date().toISOString()
    };
    const updated = [...savedDashboards.filter(d => d.id !== id), newDashboard];
    setSavedDashboards(updated);
    localStorage.setItem('savedDashboards', JSON.stringify(updated));
  };

  // Dashboard sil
  const deleteDashboard = (id) => {
    const updated = savedDashboards.filter(d => d.id !== id);
    setSavedDashboards(updated);
    localStorage.setItem('savedDashboards', JSON.stringify(updated));
  };

  // Dashboard düzenlemeye başla
  const startEditingDashboard = (dashboard) => {
    setEditingDashboardId(dashboard.id);
    setEditingTitle(dashboard.title);
  };

  // Dashboard düzenlemeyi kaydet
  const saveEditingDashboard = () => {
    if (!editingTitle.trim()) return;
    const updated = savedDashboards.map(d =>
      d.id === editingDashboardId ? { ...d, title: editingTitle.trim() } : d
    );
    setSavedDashboards(updated);
    localStorage.setItem('savedDashboards', JSON.stringify(updated));
    setEditingDashboardId(null);
    setEditingTitle('');
  };

  // Dashboard düzenlemeyi iptal et
  const cancelEditingDashboard = () => {
    setEditingDashboardId(null);
    setEditingTitle('');
  };

  // Dashboard aç
  const openDashboard = async (dashboard) => {
    setDashboardLoading(true);
    setSelectedDashboard(null);
    try {
      const response = await axios.post(`${API_URL}/api/embed-url`, {
        type: 'dashboard',
        id: dashboard.id
      });
      if (response.data.success) {
        setSelectedDashboard({
          ...dashboard,
          embedUrl: response.data.embed_url
        });
      }
    } catch (error) {
      console.error('Dashboard açma hatası:', error);
      alert('Dashboard açılamadı: ' + error.message);
    } finally {
      setDashboardLoading(false);
    }
  };

  useEffect(() => {
    checkConnection();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Mesajlardan dashboard ID'lerini çıkar ve kaydet
  useEffect(() => {
    const lastMessage = messages[messages.length - 1];
    if (lastMessage?.role === 'assistant') {
      const dashboardMatch = lastMessage.content.match(/\/dashboards\/(\d+)/);
      if (dashboardMatch) {
        const dashboardId = dashboardMatch[1];
        // Dashboard başlığı olarak tarih ve saat kullan
        const now = new Date();
        const title = now.toLocaleString('tr-TR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
        saveDashboard(dashboardId, title);
      }
    }
  }, [messages]);

  const checkConnection = async () => {
    try {
      await axios.get(`${API_URL}/api/health`);
      setConnected(true);
    } catch (error) {
      setConnected(false);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');

    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);
    setLoadingSteps([]);

    try {
      const headers = { 'Content-Type': 'application/json' };
      const idToken = localStorage.getItem('authToken');
      if (idToken) headers['Authorization'] = `Bearer ${idToken}`;

      const response = await fetch(`${API_URL}/api/chat`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ message: userMessage, history: messages, conversationId: activeConversationId })
      });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();

        for (const line of lines) {
          if (!line.trim()) continue;
          try {
            const event = JSON.parse(line);
            if (event.type === 'step') {
              setLoadingSteps(prev => [...prev, event.message]);
            } else if (event.type === 'done') {
              setMessages(prev => [...prev, {
                role: 'assistant',
                content: event.response,
                userQuery: userMessage
              }]);
              if (event.conversationId) {
                const isNewConversation = event.conversationId !== activeConversationId;
                setActiveConversationId(event.conversationId);
                if (isNewConversation) fetchConversations();
              }
            } else if (event.type === 'error') {
              setMessages(prev => [...prev, {
                role: 'assistant',
                content: '❌ Bir hata oluştu: ' + event.error
              }]);
            }
          } catch (e) {
            // parse hatası, atla
          }
        }
      }
    } catch (error) {
      console.error('Error:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: '❌ Bir hata oluştu: ' + error.message
      }]);
    } finally {
      setLoading(false);
      setLoadingSteps([]);
    }
  };

  const handleExampleClick = (question) => {
    setInput(question);
  };

  const exampleCategories = [
    {
      title: "GRP/SOV",
      questions: [
        "Getir'in 2025 yılı ay bazlı 20+ABC1 GRP ve mevsimselliği",
        "Getir'in 2025 yılı dikey bazlı 20+ABC1 GRP ve toplam Getir GRP'sine oranları ",
        "Yemeksepeti'nin 2025 yılı ay bazlı 20+ABC1 GRP ve mevsimselliği",
        "Migros'un 2025 yılı ay bazlı 20+ABC1 GRP ve pay dağılımları",
        "Migros'un 2025 yılı dikey bazlı 20+ABC1 GRP ve toplam Migros GRP'sine oranları"
      ]
    },
    {
      title: "Bütçe",
      questions: [
        "Getir'in Haziran 2025 dönemine ait dikey bazlı bütçe harcamaları",
        "Migros'un Haziran 2025 dönemine ait dikey bazlı bütçe harcamaları",
        "YemekSepeti'nin Haziran 2025 dönemine ait dikey bazlı bütçe harcamaları"
      ]
    },
    {
      title: "Online & Offline Market",
      questions: [
        "Market kategorisinin 2025 yılı ay bazlı bütçe ve SOS dağılımları",
        "Market kategorisinin 2025 Aralık 20+ABC1 GRP ve SOV dağılımları",
        "Market kategorisinin Yıl bazlı imaj/promo 20+ABC1 GRP dağılımları",
        "Market kategorisinin 2024 vs. 2025 yılı karşılaştırmalı toplam 20+ABC1 GRP ve SOV dağılımları",
        "Market kategorisinin Yıl kırılımlı toplam süreleri ve radyo SOV dağılımları",
        "Market kategorisinin Radyo 2025 yılı ay kırılımlı toplam süreleri ve SOV dağılımları",
        "2025 yılı Market kategorisinin Mecra bazlı bütçe dağılımları ve payları",
        "Market kategorisinin Discount markets ve diğer market rakipleri 2025 yılı ay kırılımlı SOV değerleri",
        "2025 yılı market kategorisi ortalama kopya süreleri nedir?",
        "2025 yılı market kategorisi impression değerleri nedir?"
      ]
    },
    {
      title: "4 Ana Marka Yemek",
      questions: [
        "4 ana marka için yemek kategorisindeki 2025 yılı ay bazlı bütçe ve SOS kırılımları",
        "4 ana marka için yemek kategorisindeki 2025 Aralık 20+ABC1 GRP ve SOV dağılımları",
        "4 ana marka için yemek kategorisindeki Yıl bazlı imaj/promo GRP dağılımları",
        "4 ana marka için yemek kategorisindeki 2024 vs. 2025 yılı karşılaştırmalı toplam 20+ABC1 GRP ve SOV dağılımları",
        "4 ana marka için yemek kategorisindeki Radyo 2025 yılı ay kırılımlı toplam süreleri ve SOV dağılımları",
        "4 ana marka için yemek kategorisindeki Mecra bazlı bütçe dağılımları ve payları",
        "2025 yılı 4 ana marka yemek ortalama kopya süreleri nedir?",
        "2025 yılı 4 ana marka yemek impression değerleri nedir?"
      ]
    },
    {
      title: "4 Ana Marka",
      questions: [
        "4 ana marka için 2025 yılı ay bazlı bütçe ve SOS kırılımları",
        "4 ana marka için Son 3 yıla ait bütçe dağılımları",
        "4 ana marka için 2025 Aralık 20+ABC1 GRP ve SOV dağılımları",
        "4 ana marka için 2024 vs. 2025 yılı karşılaştırmalı toplam 20+ABC1 GRP ve SOV dağılımları",
        "4 ana marka için 2025 yılı ay kırılımlı toplam radyo süreleri ve SOV dağılımları",
        "4 ana marka için 2025 yılı Mecra bazlı bütçe dağılımları ve payları",
        "4 ana marka için 2025 yılı ay bazlı dikey kırılımlı 20+ABC1 GRP dağılımları",
        "2025 yılı 4 ana marka ortalama kopya süreleri nedir?",
        "2025 yılı 4 ana marka impression değerleri nedir?"
      ]
    }
  ];
  // const exampleCategories = [
  //   {
  //     title: "GRP/SOV",
  //     questions: [
  //       "Getir'in 2025 yılı ay bazlı GRP ve mevsimselliği",
  //       "Getir'in 2025 yılı dikey bazlı GRP ve toplam Getir GRP'sine oranları ",
  //       "Yemeksepeti'nin 2025 yılı ay bazlı GRP ve mevsimselliği",
  //       "Migros'un 2025 yılı ay bazlı GRP ve pay dağılımları",
  //       "Migros'un 2025 yılı dikey bazlı GRP ve toplam Migros GRP'sine oranları"
  //     ]
  //   },
  //   {
  //     title: "Bütçe",
  //     questions: [
  //       "Getir'in Haziran 2025 dönemine ait dikey bazlı bütçe harcamaları",
  //       "Migros'un Haziran 2025 dönemine ait dikey bazlı bütçe harcamaları",
  //       "YemekSepeti'nin Haziran 2025 dönemine ait dikey bazlı bütçe harcamaları"
  //     ]
  //   },
  //   {
  //     title: "Online & Offline Market",
  //     questions: [
  //       "Market kategorisinin 2025 yılı ay bazlı bütçe ve SOS dağılımları",
  //       "Market kategorisinin 2025 Aralık GRP ve SOV dağılımları",
  //       "Market kategorisinin Yıl bazlı imaj/promo GRP dağılımları",
  //       "Market kategorisinin 2024 vs. 2025 yılı karşılaştırmalı toplam GRP ve SOV dağılımları",
  //       "Market kategorisinin Yıl kırılımlı toplam süreleri ve radyo SOV dağılımları",
  //       "Market kategorisinin Radyo 2025 yılı ay kırılımlı toplam süreleri ve SOV dağılımları",
  //       "2025 yılı Market kategorisinin Mecra bazlı bütçe dağılımları ve payları",
  //       "Market kategorisinin Discount markets ve diğer market rakipleri 2025 yılı ay kırılımlı SOV değerleri"
  //     ]
  //   },
  //   {
  //     title: "4 Ana Marka Yemek",
  //     questions: [
  //       "4 ana marka için yemek kategorisindeki 2025 yılı ay bazlı bütçe ve SOS kırılımları",
  //       "4 ana marka için yemek kategorisindeki 2025 Aralık GRP ve SOV dağılımları",
  //       "4 ana marka için yemek kategorisindeki Yıl bazlı imaj/promo GRP dağılımları",
  //       "4 ana marka için yemek kategorisindeki 2024 vs. 2025 yılı karşılaştırmalı toplam GRP ve SOV dağılımları",
  //       "4 ana marka için yemek kategorisindeki Radyo 2025 yılı ay kırılımlı toplam süreleri ve SOV dağılımları",
  //       "4 ana marka için yemek kategorisindeki Mecra bazlı bütçe dağılımları ve payları"
  //     ]
  //   },
  //   {
  //     title: "4 Ana Marka",
  //     questions: [
  //       "4 ana marka için 2025 yılı ay bazlı bütçe ve SOS kırılımları",
  //       "4 ana marka için Son 3 yıla ait bütçe dağılımları",
  //       "4 ana marka için 2025 Aralık GRP ve SOV dağılımları",
  //       "4 ana marka için 2024 vs. 2025 yılı karşılaştırmalı toplam GRP ve SOV dağılımları",
  //       "4 ana marka için 2025 yılı ay kırılımlı toplam radyo süreleri ve SOV dağılımları",
  //       "4 ana marka için 2025 yılı Mecra bazlı bütçe dağılımları ve payları",
  //       "4 ana marka için 2025 yılı ay bazlı dikey kırılımlı GRP dağılımları"
  //     ]
  //   }
  // ];

  if (authChecking) {
    return <div className="login-screen" />;
  }

  if (!authUser && !guestMode) {
    return (
      <div className="login-screen">
        <div className="login-card">
          <h1>Getir</h1>
          <p>Devam etmek için giriş yapın</p>
          <form className="login-form" onSubmit={handleLogin}>
            <input
              type="email"
              placeholder="E-posta"
              value={loginEmail}
              onChange={(e) => setLoginEmail(e.target.value)}
              autoComplete="username"
              required
            />
            <input
              type="password"
              placeholder="Şifre"
              value={loginPassword}
              onChange={(e) => setLoginPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
            {loginError && <p className="login-error">{loginError}</p>}
            <button type="submit" className="okta-login-main-btn" disabled={loginLoading}>
              {loginLoading ? 'Giriş yapılıyor...' : 'Giriş Yap'}
            </button>
          </form>
          {/* <button className="okta-login-main-btn" onClick={handleOktaLogin}>Okta ile Giriş Yap</button> */}
          <button className="guest-btn" onClick={continueAsGuest}>Giriş yapmadan devam et</button>
        </div>
      </div>
    );
  }

  return (
    <div className="app-layout">
      {authUser && sidebarOpen && (
        <div className="sidebar-backdrop" onClick={() => setSidebarOpen(false)} />
      )}
      {authUser && (
        <aside className={`conversation-sidebar ${sidebarOpen ? '' : 'collapsed'}`}>
          <div className="sidebar-header">
            {sidebarOpen && <button className="new-chat-btn" onClick={startNewConversation}>+ Yeni Sohbet</button>}
            <button className="sidebar-toggle-btn" onClick={() => setSidebarOpen(!sidebarOpen)} title={sidebarOpen ? 'Kenar çubuğunu daralt' : 'Kenar çubuğunu genişlet'}>
              {sidebarOpen ? '◀' : '▶'}
            </button>
          </div>
          {sidebarOpen && (
            <div className="conversation-list">
              {conversations.length === 0 ? (
                <p className="no-conversations">Henüz kayıtlı sohbet yok.</p>
              ) : (
                conversations.map(conv => (
                  <div
                    key={conv._id}
                    className={`conversation-item ${conv._id === activeConversationId ? 'active' : ''}`}
                    onClick={() => loadConversation(conv._id)}
                  >
                    <span className="conversation-title">{conv.title}</span>
                    <button className="conversation-delete-btn" onClick={(e) => deleteConversation(conv._id, e)} title="Sil">
                      🗑️
                    </button>
                  </div>
                ))
              )}
            </div>
          )}
        </aside>
      )}
    <div className="app">
      <header className="header">
        {authUser && (
          <button
            className="mobile-sidebar-toggle"
            onClick={() => setSidebarOpen(true)}
            title="Sohbet geçmişi"
          >
            ☰
          </button>
        )}
        <h1>Getir</h1>
        {/* <p>TV Reklam Verilerinizi Analiz Edin</p> */}
        {savedDashboards.length > 0 && (
          <button
            className="dashboards-btn"
            onClick={() => setShowDashboardPanel(!showDashboardPanel)}
          >
            📁 Dashboard'larım ({savedDashboards.length})
          </button>
        )}
        {authUser ? (
          <div className="okta-user">
            <span>{authUser.name || authUser.email}</span>
            <button className="okta-logout-btn" onClick={handleLogout}>Çıkış Yap</button>
          </div>
        ) : (
          <button
            className="okta-btn"
            onClick={() => { localStorage.removeItem('guestMode'); setGuestMode(false); }}
          >
            Giriş Yap
          </button>
        )}
      </header>

      {/* Dashboard Panel */}
      {showDashboardPanel && (
        <div className="dashboard-panel">
          <div className="dashboard-panel-header">
            <h3>📁 Kaydedilmiş Dashboard'larım</h3>
            <button onClick={() => setShowDashboardPanel(false)}>✕</button>
          </div>
          <div className="dashboard-list">
            {savedDashboards.length === 0 ? (
              <p className="no-dashboards">Henüz kaydedilmiş dashboard yok.</p>
            ) : (
              savedDashboards.map(dashboard => (
                <div key={dashboard.id} className="dashboard-item">
                  <div className="dashboard-info">
                    {editingDashboardId === dashboard.id ? (
                      <input
                        type="text"
                        className="dashboard-title-input"
                        value={editingTitle}
                        onChange={(e) => setEditingTitle(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') saveEditingDashboard();
                          if (e.key === 'Escape') cancelEditingDashboard();
                        }}
                        autoFocus
                      />
                    ) : (
                      <span className="dashboard-title">{dashboard.title}</span>
                    )}
                    
                  </div>
                  <div className="dashboard-actions">
                    {editingDashboardId === dashboard.id ? (
                      <>
                        <button
                          className="save-btn"
                          onClick={saveEditingDashboard}
                        >
                          ✓
                        </button>
                        <button
                          className="cancel-btn"
                          onClick={cancelEditingDashboard}
                        >
                          ✕
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          className="edit-btn"
                          onClick={() => startEditingDashboard(dashboard)}
                        >
                          ✏️
                        </button>
                        <button
                          className="view-btn"
                          onClick={() => openDashboard(dashboard)}
                          disabled={dashboardLoading}
                        >
                          {dashboardLoading ? '⏳' : '👁️'}
                        </button>
                        <button
                          className="delete-btn"
                          onClick={() => deleteDashboard(dashboard.id)}
                        >
                          🗑️
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Seçilen Dashboard Modal */}
      {selectedDashboard && (
        <>
          <div className="embed-overlay" onClick={() => setSelectedDashboard(null)} />
          <div className="looker-embed expanded">
            <div className="embed-header">
              <span className="embed-title">📊 {selectedDashboard.title}</span>
              <div className="embed-actions">
                <button 
                  className="embed-btn"
                  onClick={() => setSelectedDashboard(null)}
                >
                  ✕ Kapat
                </button>
              </div>
            </div>
            <div className="embed-content">
              <iframe
                src={selectedDashboard.embedUrl}
                title="Looker Dashboard"
                allow="fullscreen"
              />
            </div>
          </div>
        </>
      )}

      {/* <div className={`status ${connected ? 'connected' : 'disconnected'}`}>
        {connected === null ? '⏳ Bağlantı kontrol ediliyor...' : 
         connected ? '✅ Backend bağlı' : '❌ Backend bağlantısı yok'}
      </div> */}

      <div className="chat-container">
        <div className="messages">
          {messages.length === 0 && (
            <div className="message assistant welcome">
              <div className="welcome-icon">👋</div>
              <div className="welcome-text">
                <strong>Merhaba!</strong> 
                <br /><br />
                📈 Sorularınıza otomatik <strong>yanıtlar</strong> oluşturabilirim!
                <br />
                Örnek: "Aylara göre GRP dağılımı" veya "Markaların harcama karşılaştırması"
              </div>
            </div>
          )}
          
          {messages.map((msg, index) => (
            <div key={index} className={`message ${msg.role}`}>
              {msg.role === 'assistant' ? (
                <MessageContent content={msg.content} userQuery={msg.userQuery} />
              ) : (
                msg.content
              )}
            </div>
          ))}
          
          {loading && (
            <div className="loading">
              <div className="loading-steps">
                {loadingSteps.map((step, i) => (
                  <div key={i} className={`loading-step ${i === loadingSteps.length - 1 ? 'active' : 'completed'}`}>
                    <span className="step-icon">
                      {i === loadingSteps.length - 1 ? (
                        <span className="step-spinner"></span>
                      ) : (
                        '✓'
                      )}
                    </span>
                    <span className="step-text">{step}</span>
                  </div>
                ))}
                {loadingSteps.length === 0 && (
                  <div className="loading-step active">
                    <span className="step-icon"><span className="step-spinner"></span></span>
                    <span className="step-text">Bağlantı kuruluyor...</span>
                  </div>
                )}
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        <div className="examples">
          <span className="examples-label">Kategoriler:</span>
          {exampleCategories.map((cat, i) => (
            <button key={i} onClick={() => setActiveCategoryPopup(i)}>
              {cat.title}
            </button>
          ))}
        </div>

        {activeCategoryPopup !== null && (
          <>
            <div className="category-overlay" onClick={() => setActiveCategoryPopup(null)} />
            <div className="category-popup">
              <div className="category-popup-header">
                <h3>{exampleCategories[activeCategoryPopup].title}</h3>
                <button onClick={() => setActiveCategoryPopup(null)}>✕</button>
              </div>
              <div className="category-popup-list">
                {exampleCategories[activeCategoryPopup].questions.map((q, i) => (
                  <button
                    key={i}
                    className="category-question-btn"
                    onClick={() => {
                      handleExampleClick(q);
                      setActiveCategoryPopup(null);
                    }}
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        <form className="input-area" onSubmit={sendMessage}>
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Örn: Aylara göre Getir'in GRP'si nedir?"
            disabled={loading || !connected}
          />
          <button type="submit" disabled={loading || !connected || !input.trim()}>
            {loading ? '⏳' : '🚀'}
          </button>
        </form>
      </div>
    </div>
    </div>
  );
}

export default App;
